import { Container } from '@cleartrip/ct-design-components'
import React, { useCallback, useMemo, useState } from 'react'

import { CloseIcon } from '@/assets/icons/CloseIcon'

import { CoverageTab } from './constants'
import Eligibility from './Eligibility'
import Faq from './FAQ'
import HowToClaim from './HowToClaim'
import PolicyCoverageDetails from './PolicyCoverageDetails'
import SectionContainer from './SectionContainer'
import Sidebar from './Sidebar'
import { useSectionObserver } from './useSectionObserver'
import VdcHeader from './VdcHeader'
import WhatIsNotIncluded from './WhatIsNotIncluded'
import styles from './VdcEducationPage.module.scss'

interface VdcEducationPageProps {
  onClose?: () => void
}

function VdcEducationPage({ onClose }: VdcEducationPageProps) {
  const [activeTab, setActiveTab] = useState<CoverageTab>('covered')
  const [expandedFaqs, setExpandedFaqs] = useState<Set<number>>(new Set())

  const { activeSection, scrollToSection, attachSectionRef, setScrollContainerRef } = useSectionObserver()

  const toggleFaq = useCallback((index: number) => {
    setExpandedFaqs(prev => {
      const next = new Set(prev)
      next.has(index) ? next.delete(index) : next.add(index)
      return next
    })
  }, [])

  const sectionConfigs = useMemo(() => [
    { id: 'eligibility' as const, title: 'Eligibility criteria', content: <Eligibility /> },
    { id: 'howToClaim' as const, title: 'How to claim', content: <HowToClaim /> },
    { id: 'notIncluded' as const, title: 'What is not included', content: <WhatIsNotIncluded /> },
    { id: 'coverage' as const, title: 'Policy coverage details', content: <PolicyCoverageDetails activeTab={activeTab} onTabChange={setActiveTab} /> },
    { id: 'faqs' as const, title: 'FAQs', content: <Faq expandedFaqs={expandedFaqs} onToggle={toggleFaq} /> }
  ], [activeTab, expandedFaqs, toggleFaq])

  return (
    <Container ref={setScrollContainerRef} className={`${styles.vdcEducationPage} flex flex-column`}>
      {/* Fixed close button - stays at top-right while scrolling */}
      {onClose && (
        <Container className={styles['vdcEducationPage__closeBtn']} onClick={onClose}>
          <CloseIcon />
        </Container>
      )}
      <VdcHeader />
      <Container className={`${styles.mainContent} flex`}>
        <Container className={styles.sidebarWrapper}>
          <Sidebar activeSection={activeSection} onSectionClick={scrollToSection} />
        </Container>
        <Container className={`${styles.contentArea} flex flex-column`}>
          {sectionConfigs.map(({ id, title, content }) => (
            <SectionContainer 
              key={id}
              id={id} 
              sectionRef={attachSectionRef(id)} 
              title={title}
            >
              {content}
            </SectionContainer>
          ))}
        </Container>
      </Container>
    </Container>
  )
}

export default VdcEducationPage
